/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Component } from 'react'

import { EuiToolTip } from '@elastic/eui'

import type { ReactNode } from 'react'

// Styles
const wrapperStyles = css({
  width: '100%',
  height: '100%',
  '.euiToolTipAnchor': {
    width: '100%',
    height: '100%',
    display: 'block',
  },
})

const wrapperInlineTextStyles = css({
  display: 'inline-block',
  verticalAlign: 'middle',
  height: '1rem',
  width: '8rem',
})

const contentStyles = css({
  width: '100%',
  height: '100%',
  '&::before': {
    content: '" "',
    whiteSpace: 'pre',
    pointerEvents: 'none',
    userSelect: 'none',
  },
})

const contentInlineTextStyles = css({
  padding: 0,
})

const tooltipStyles = css({
  width: '550px',
  maxWidth: 'calc(100% - 10px)',
})

interface Props {
  children: ReactNode
  forInlineText?: boolean
}

type State = {
  hasError: boolean
  error: Error | null
}

export class CuiSmallErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
  }

  componentDidCatch(error) {
    this.setState({
      hasError: true,
      error,
    })
  }

  render() {
    const { children, forInlineText = false, ...rest } = this.props
    const { hasError, error } = this.state

    // could TS be any dumber?
    const typedError = error as Error | null
    const tooltipContent = (typedError instanceof Error && typedError.stack) || null

    if (hasError) {
      return (
        <div css={[wrapperStyles, forInlineText && wrapperInlineTextStyles]}>
          <EuiToolTip css={tooltipStyles} content={tooltipContent}>
            <div
              className='euiErrorBoundary'
              css={[contentStyles, forInlineText && contentInlineTextStyles]}
              {...rest}
            />
          </EuiToolTip>
        </div>
      )
    }

    return children
  }
}
